import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";

import familySupportAPlusAsset from "@/assets/family-support-cutout.webp.asset.json";
import learnerAPlusV2Asset from "@/assets/learner-a-plus-cutout.webp.asset.json";
import schoolSupportAPlusAsset from "@/assets/school-support-cutout.webp.asset.json";
import { ImageBox } from "@/components/site/ImageBox";
import { PageHero } from "@/components/site/PageHero";
import { SiteFooter } from "@/components/site/SiteFooter";
import { FaqSection, faqJsonLd } from "@/components/site/FaqSection";
import { portalFaqs } from "@/content/faqs";

export const Route = createFileRoute("/connected-portals")({
  head: () => ({
    meta: [
      { title: "Connected Portals — adaptED" },
      {
        name: "description",
        content:
          "adaptED Connected Portals: linked experiences for learners, parents and schools, each with its own view of progress.",
      },
      { property: "og:title", content: "Connected Portals — adaptED" },
      {
        property: "og:description",
        content: "Learner, parent and school portals working together in one place.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://www.adapted.info/connected-portals" },
      { property: "og:site_name", content: "adaptED" },
      { name: "twitter:title", content: "Connected Portals — adaptED" },
      { name: "twitter:description", content: "Learner, parent and school portals working together in one place." },
    ],
    links: [{ rel: "canonical", href: "https://www.adapted.info/connected-portals" }],
    scripts: [faqJsonLd(portalFaqs, "https://www.adapted.info/connected-portals")],
  }),
  component: ConnectedPortalsPage,
});

const portals = [
  {
    name: "Learners",
    title: "Learners",
    description:
      "See what you know, uncover the gaps holding you back and follow a personalised path towards your academic goals.",
    cta: "Explore the Learner Portal",
    features: ["Personalised learning path", "Knowledge-gap analysis", "CAPS-aligned content", "Safe, learning-first AI support"],
  },
  {
    name: "Parents",
    title: "Parents",
    description:
      "Understand how your child is progressing, where they may need support and how you can help without having to guess.",
    cta: "Explore the Parent Portal",
    features: ["Clear progress overview", "Knowledge-gap alerts", "Actionable support tips", "Peace of mind, always-on"],
  },
  {
    name: "Schools",
    title: "Schools",
    description:
      "Give teachers a clearer view of learner progress, class-wide knowledge gaps and where intervention can have the greatest impact.",
    cta: "Explore the School Portal",
    features: ["Class-wide insights", "Targeted intervention signals", "Progress dashboards", "Data-driven teaching support"],
  },
];

function ConnectedPortalsPage() {
  return (
    <main id="top">
      <PageHero
        label=""
        title="One learner. One support system. Three connected portals."
        paragraph="adaptED connects learners, parents and schools through a shared view of learning progress, knowledge gaps and the support needed next."
        quote="I am because we are, and since we are, therefore I am."
        quoteSource="Ubuntu — Nguni Bantu philosophy"
        quoteNote="It takes a village to raise a [learner]"
      />

      {portals.map((portal, i) => (
        <section
          key={portal.name}
          className={`relative py-12 lg:py-24 ${i % 2 === 0 ? "surface-warm" : "bg-background/90"}`}
        >
          <div className="mx-auto grid max-w-7xl items-center gap-6 px-6 lg:gap-14 lg:grid-cols-2">
            <div className={`flex flex-col items-center text-center lg:items-start lg:text-left ${i % 2 === 0 ? "" : "lg:order-2"}`}>
              <h2 className="font-display text-4xl leading-[1.05] text-foreground sm:text-5xl">
                {portal.title}
              </h2>
              <p className="mt-5 max-w-md text-lg leading-relaxed text-muted-foreground">
                {portal.description}
              </p>
              <ul className="mt-8 space-y-3">
                {portal.features.map((feature) => (
                  <li key={feature} className="flex items-center justify-center gap-3 text-sm text-muted-foreground lg:justify-start">
                    <span className="size-1.5 rounded-full bg-primary" />
                    {feature}
                  </li>
                ))}
              </ul>
              <Link
                to="/join-the-pilot"
                search={{ type: portal.name === "Learners" ? "learner" : portal.name === "Parents" ? "parent" : "school" }}
                className="mt-9 inline-flex items-center gap-2 rounded-full bg-ink px-7 py-3.5 text-sm font-semibold text-ink-foreground transition-transform hover:-translate-y-0.5"
              >
                {portal.cta} <ArrowRight className="size-4" />
              </Link>
            </div>

            {i === 0 ? (
              <img
                  loading="lazy"
                  decoding="async"
                src={learnerAPlusV2Asset.url}
                alt="Learner using adaptED to achieve academic success"
                className={`h-auto w-full object-contain lg:aspect-[4/3] ${
                  i % 2 === 0 ? "" : "lg:order-1"
                }`}
              />
            ) : i === 1 ? (
              <img
                  loading="lazy"
                  decoding="async"
                src={familySupportAPlusAsset.url}
                alt="Family supporting a learner with adaptED"
                className={`h-auto w-full object-contain lg:aspect-[4/3] ${
                  i % 2 === 0 ? "" : "lg:order-1"
                }`}
              />
            ) : i === 2 ? (
              <img
                  loading="lazy"
                  decoding="async"
                src={schoolSupportAPlusAsset.url}
                alt="School community supporting a learner with adaptED"
                className={`h-auto w-full object-contain lg:aspect-[4/3] ${
                  i % 2 === 0 ? "" : "lg:order-1"
                }`}
              />
            ) : (
              <ImageBox
                className={`aspect-[4/3] w-full shadow-[var(--shadow-card)] ${
                  i % 2 === 0 ? "" : "lg:order-1"
                }`}
              />
            )}
          </div>
        </section>
      ))}

      <FaqSection
        items={portalFaqs}
        heading="Questions about the portals"
        paragraph="How the learner, parent and school portals fit together."
      />
      <SiteFooter />
    </main>
  );
}
